import { useMemo, useState } from "react";
import { api, type ImportEventsResponse, type ScheduleEvent } from "../api";
import {
  buildRows,
  guessMapping,
  parseCsv,
  type FieldName,
  type ParsedRow,
} from "../lib/scheduleCsv";

const inputClass = "mt-1 w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm";
const labelClass = "block text-xs font-semibold text-slate-600";

const FIELDS: { key: FieldName; label: string; required?: boolean }[] = [
  { key: "eventDate", label: "Date", required: true },
  { key: "startTime", label: "Start time" },
  { key: "opponent", label: "Opponent" },
  { key: "location", label: "Location" },
  { key: "capColor", label: "Cap color" },
  { key: "levelLabel", label: "Level" },
  { key: "notes", label: "Notes" },
];

/**
 * Paste or pick a CSV, map its columns, see what will happen, import.
 *
 * The preview is the point. Everything this does to a coach's data — reading
 * "10/4" as a date, "Home" as white caps, matching a row to a game already on
 * the schedule — is a guess, and each one is shown before anything is written.
 */
export function ScheduleCsvImport({
  clubId,
  scheduleId,
  existingEvents,
  onImported,
}: {
  clubId: string;
  scheduleId: string;
  existingEvents: ScheduleEvent[];
  onImported: () => void | Promise<void>;
}) {
  const [text, setText] = useState("");
  const [hasHeader, setHasHeader] = useState(true);
  const [manualMapping, setManualMapping] = useState<Partial<Record<FieldName, number>> | null>(null);
  const [fallbackYear, setFallbackYear] = useState(String(new Date().getFullYear()));
  const [importing, setImporting] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const grid = useMemo(() => parseCsv(text), [text]);
  const headerRow = hasHeader ? grid[0] ?? [] : [];
  const dataRows = hasHeader ? grid.slice(1) : grid;

  // Column labels for the mapping selects: the header text when there is one,
  // else positional names, so an unheadered file is still mappable.
  const columnLabels = useMemo(() => {
    const width = Math.max(0, ...grid.map((r) => r.length));
    return Array.from({ length: width }, (_, i) =>
      hasHeader ? (headerRow[i]?.trim() || `Column ${i + 1}`) : `Column ${i + 1}`
    );
  }, [grid, hasHeader, headerRow]);

  const mapping = manualMapping ?? (hasHeader ? guessMapping(headerRow) : {});

  const rows: ParsedRow[] = useMemo(
    () => (dataRows.length ? buildRows(dataRows, mapping, Number(fallbackYear) || new Date().getFullYear()) : []),
    [dataRows, mapping, fallbackYear]
  );

  // Mirrors the server's merge rule so the preview can say "update" rather than
  // surprising the coach with a changed row after the fact.
  const existingKeys = useMemo(
    () =>
      new Set(
        existingEvents.map((e) => `${e.eventDate}|${(e.opponent ?? "").trim().toLowerCase()}`)
      ),
    [existingEvents]
  );

  const statusOf = (r: ParsedRow): "error" | "update" | "new" => {
    if (r.error) return "error";
    return existingKeys.has(`${r.eventDate}|${r.opponent.trim().toLowerCase()}`) ? "update" : "new";
  };

  const importable = rows.filter((r) => !r.error);
  const errorCount = rows.length - importable.length;
  const updateCount = importable.filter((r) => statusOf(r) === "update").length;

  function setField(field: FieldName, value: string) {
    const next = { ...mapping };
    if (value === "") delete next[field];
    else next[field] = Number(value);
    setManualMapping(next);
  }

  async function handleFile(file: File) {
    setText(await file.text());
    // A new file's columns have nothing to do with the last one's.
    setManualMapping(null);
    setMessage(null);
  }

  async function handleImport() {
    setImporting(true);
    setMessage(null);
    try {
      const res = await api<ImportEventsResponse>(
        `/v1/clubs/${clubId}/schedules/${scheduleId}/events/import`,
        {
          method: "POST",
          body: JSON.stringify({
            events: importable.map((r) => ({
              eventDate: r.eventDate,
              startTime: r.startTime,
              opponent: r.opponent,
              location: r.location,
              capColor: r.capColor,
              levelLabel: r.levelLabel,
              notes: r.notes,
            })),
          }),
        }
      );
      setMessage({
        ok: true,
        text: `Imported ${res.total} games — ${res.created} added, ${res.updated} updated.`,
      });
      setText("");
      setManualMapping(null);
      await onImported();
    } catch (err) {
      setMessage({ ok: false, text: err instanceof Error ? err.message : "Import failed" });
    } finally {
      setImporting(false);
    }
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <h3 className="mb-1 text-sm font-semibold">Import games from a spreadsheet</h3>
      <p className="mb-4 text-xs text-slate-500">
        Paste CSV or choose a file. Games already on this schedule with the same date and opponent
        are updated rather than duplicated, so you can re-import a corrected sheet.
      </p>

      <div className="flex flex-col gap-3">
        <div>
          <label className={labelClass}>Paste CSV</label>
          <textarea
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setManualMapping(null);
              setMessage(null);
            }}
            rows={4}
            className={`${inputClass} font-mono text-xs`}
            placeholder={"Date,Time,Opponent,Pool\n10/4/2026,9:00 AM,Danville Dolphins,San Ramon"}
          />
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className={labelClass}>…or choose a file</label>
            <input
              type="file"
              accept=".csv,text/csv,text/plain"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleFile(f);
              }}
              className="mt-1 text-sm"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={hasHeader}
              onChange={(e) => {
                setHasHeader(e.target.checked);
                setManualMapping(null);
              }}
            />
            First row is a header
          </label>
          <div>
            <label className={labelClass}>Year for dates without one</label>
            <input
              type="number"
              value={fallbackYear}
              onChange={(e) => setFallbackYear(e.target.value)}
              className={`${inputClass} w-28`}
            />
          </div>
        </div>
      </div>

      {grid.length > 0 && (
        <>
          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            {FIELDS.map((f) => (
              <div key={f.key}>
                <label className={labelClass}>
                  {f.label}
                  {f.required && <span className="text-rose-600"> *</span>}
                </label>
                <select
                  value={mapping[f.key] ?? ""}
                  onChange={(e) => setField(f.key, e.target.value)}
                  className={inputClass}
                >
                  <option value="">— not in file —</option>
                  {columnLabels.map((label, i) => (
                    <option key={i} value={i}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          {rows.length > 0 && (
            <div className="mt-5">
              <div className="mb-2 flex flex-wrap items-center gap-3 text-sm">
                <span className="font-semibold">
                  {importable.length} of {rows.length} ready
                </span>
                {updateCount > 0 && (
                  <span className="text-slate-600">{updateCount} will update existing games</span>
                )}
                {errorCount > 0 && (
                  <span className="text-rose-600">{errorCount} skipped — fix and re-paste</span>
                )}
              </div>

              <div className="max-h-72 overflow-auto rounded-lg border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="px-3 py-2">#</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Time</th>
                      <th className="px-3 py-2">Opponent</th>
                      <th className="px-3 py-2">Caps</th>
                      <th className="px-3 py-2">Location</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const status = statusOf(r);
                      return (
                        <tr
                          key={r.rowNumber}
                          className={`border-t border-slate-100 ${status === "error" ? "bg-rose-50" : ""}`}
                        >
                          <td className="px-3 py-2 tabular-nums text-slate-400">{r.rowNumber}</td>
                          <td className="px-3 py-2">
                            {status === "error" ? (
                              <span className="text-xs font-semibold text-rose-600">{r.error}</span>
                            ) : status === "update" ? (
                              <span className="text-xs font-semibold text-amber-700">Update</span>
                            ) : (
                              <span className="text-xs font-semibold text-emerald-700">New</span>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2">{r.eventDate || "—"}</td>
                          <td className="whitespace-nowrap px-3 py-2 text-slate-600">
                            {r.startTime || "TBD"}
                          </td>
                          <td className="px-3 py-2">{r.opponent || "—"}</td>
                          <td className="px-3 py-2 text-slate-600">
                            {r.capColor === "white" ? "White" : "Dark"}
                          </td>
                          <td className="px-3 py-2 text-slate-600">{r.location || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleImport}
                  disabled={importing || importable.length === 0}
                  className="rounded-md bg-brand-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  {importing ? "Importing…" : `Import ${importable.length} game${importable.length === 1 ? "" : "s"}`}
                </button>
                {message && (
                  <p className={`text-sm ${message.ok ? "text-emerald-600" : "text-rose-600"}`}>
                    {message.text}
                  </p>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {grid.length === 0 && message && (
        <p className={`mt-3 text-sm ${message.ok ? "text-emerald-600" : "text-rose-600"}`}>
          {message.text}
        </p>
      )}
    </div>
  );
}
